/**
 * Telegram Mini App utilities: init, haptic feedback, back button, user data.
 */

const getTg = () =>
  (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;

interface TelegramWebApp {
  expand: () => void;
  ready: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    isVisible: boolean;
  };
  CloudStorage?: {
    getItem: (key: string, callback: (err: unknown, value?: string) => void) => void;
    setItem: (key: string, value: string, callback?: (err: unknown) => void) => void;
  };
  initDataUnsafe?: { start_param?: string; user?: TelegramUser };
  initData?: string;
  onEvent?: (event: string, callback: (data?: unknown) => void) => void;
  offEvent?: (event: string, callback: (data?: unknown) => void) => void;
  openInvoice?: (url: string, callback?: (status: 'paid' | 'cancelled' | 'failed' | 'pending' | string) => void) => void;
}

interface TelegramUser {
  id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  photo_url?: string;
}

export const initTelegram = () => {
  const tg = getTg();
  if (!tg) return;

  tg.expand();
  tg.setHeaderColor('#06050f');
  tg.setBackgroundColor('#06050f');
  tg.setBottomBarColor?.('#06050f');
  tg.ready();
};

export const haptic = {
  light: () => getTg()?.HapticFeedback?.impactOccurred('light'),
  medium: () => getTg()?.HapticFeedback?.impactOccurred('medium'),
  heavy: () => getTg()?.HapticFeedback?.impactOccurred('heavy'),
  success: () => getTg()?.HapticFeedback?.notificationOccurred('success'),
  error: () => getTg()?.HapticFeedback?.notificationOccurred('error'),
  warning: () => getTg()?.HapticFeedback?.notificationOccurred('warning'),
  selection: () => getTg()?.HapticFeedback?.selectionChanged(),
};

export const getTelegramUser = (): TelegramUser | undefined =>
  getTg()?.initDataUnsafe?.user;

export const getTelegramInitData = (): string | undefined => getTg()?.initData;

export const cloudStorage = {
  get: (key: string): Promise<string | undefined> =>
    new Promise((resolve) => {
      const tg = getTg();
      if (!tg?.CloudStorage) { resolve(undefined); return; }
      tg.CloudStorage.getItem(key, (_, value) => resolve(value));
    }),
  set: (key: string, value: string): Promise<void> =>
    new Promise((resolve) => {
      const tg = getTg();
      if (!tg?.CloudStorage) { resolve(); return; }
      tg.CloudStorage.setItem(key, value, () => resolve());
    }),
};

